<template>
  <div :class="['ai-chat-message', `-${message.role}`, sending ? '-sending' : '']">
    <ai-chat-trace
      v-if="trace !== null"
      class="_trace"
      :steps="trace.steps"
      :elapsed-ms="trace.elapsedMs"
      :started-at="trace.startedAt"
      :running="trace.running"
    />
    <ai-chat-attachment-tray
      v-if="attachments.length > 0"
      class="_attachments"
      :attachments="attachments"
      large
    />
    <m-editor
      v-if="hasText"
      class="_body"
      :initial-value="value"
      :allowed-content="allowedContent"
      :heading-levels="headingLevels"
      disabled
      inline-images
      disable-mobile-menu
      hide-placeholder
      :fade-in-new-text="streaming"
    />
    <div
      v-if="stopped"
      class="_stopped"
    >
      {{ $t('aiChat.stopped') }}
    </div>
    <div
      v-if="!sending && !streaming"
      class="_actions"
    >
      <m-tooltip
        v-if="!stopped"
        placement="bottom"
      >
        <m-btn
          icon="copy"
          light
          hide-border
          fab
          xs
          @click="copy"
        />
        <template #title>
          {{ $t('aiChat.copyMessage') }}
        </template>
      </m-tooltip>
      <m-tooltip
        v-if="isPrompt && hasText"
        placement="bottom"
      >
        <m-btn
          class="_save-skill"
          icon="lucide-zap"
          light
          hide-border
          fab
          xs
          @click="savingSkill = true"
        />
        <template #title>
          {{ $t('aiChat.skills.saveAsSkill') }}
        </template>
      </m-tooltip>
      <m-tooltip
        v-if="retryable"
        placement="bottom"
      >
        <m-btn
          class="_retry"
          icon="sync"
          light
          hide-border
          fab
          xs
          @click="$emit('retry')"
        />
        <template #title>
          {{ $t('aiChat.retry') }}
        </template>
      </m-tooltip>
    </div>
    <ai-chat-skill-form
      v-if="savingSkill"
      v-model:value="savingSkill"
      :prompt="message.body"
    />
  </div>
</template>

<script setup>
import AiChatAttachmentTray from '@/components/ai-chat/AiChatAttachmentTray.vue';
import AiChatSkillForm from '@/components/ai-chat/AiChatSkillForm.vue';
import AiChatTrace from '@/components/ai-chat/AiChatTrace.vue';
import MEditor from '@/components/editor/MEditor.vue';
import useSnackBar from '@/composables/snackbar';
import { aiChatMessageOutcome, aiChatMessageRole, editorNodeType } from 'shared/constants.json';
import { computed, ref } from 'vue';
import { docToHtml, docToMarkdown, markdownToDoc } from '@/lib/markdown';
import { isImageName } from '@/lib/attachment';
import { logCatch } from '@/lib/logger/logger';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  // the message is in the conversation before the write that gives it a uid comes back
  sending: {
    type: Boolean,
    default: false,
  },
  streaming: {
    type: Boolean,
    default: false,
  },
  retryable: {
    type: Boolean,
    default: false,
  },
  trace: {
    type: Object,
    default: null,
  },
});

defineEmits(['retry']);

const allowedContent = [
  editorNodeType.blockquote,
  editorNodeType.bulletList,
  editorNodeType.codeBlock,
  editorNodeType.heading,
  editorNodeType.image,
  editorNodeType.orderedList,
  editorNodeType.todoList,
  editorNodeType.horizontalRule,
  editorNodeType.table,
];

const headingLevels = [1, 2, 3, 4];

const { t } = useI18n();
const snackbar = useSnackBar();

const savingSkill = ref(false);

const stopped = computed(() => props.message.outcome === aiChatMessageOutcome.stopped);

const isPrompt = computed(() => props.message.role === aiChatMessageRole.user);
const hasText = computed(() => Boolean(isPrompt.value ? props.message.body : props.message.content));
const value = computed(() => (isPrompt.value ? props.message.body : markdownToDoc(props.message.content)));
const plainText = computed(() => (isPrompt.value ? docToMarkdown(props.message.body) : props.message.content));

const attachments = computed(() => (props.message.attachments ?? []).map((attachment) => ({
  key: attachment.uid,
  name: attachment.fileName,
  isImage: isImageName(attachment.fileName),
  url: attachment.getURL,
  uploading: false,
})));

const onCopyError = logCatch(() => snackbar.error());

// the html flavour is what pastes back into our editor with its formatting intact; the
// markdown stays on the plain text flavour for everywhere else
const copy = async () => {
  try {
    await navigator.clipboard.write([new ClipboardItem({
      'text/html': new Blob([docToHtml(value.value)], { type: 'text/html' }),
      'text/plain': new Blob([plainText.value], { type: 'text/plain' }),
    })]);
    snackbar.success(t('success.copied'));
  } catch (error) {
    onCopyError(error);
  }
};
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  .ai-chat-message {
    position: relative;
    padding: .4rem 0;

    ._actions {
      display: flex;
      margin-top: .4rem;
    }

    ._attachments {
      margin-bottom: .4rem;
    }

    ._trace {
      margin-bottom: .4rem;
    }

    ._body {
      min-width: 0;
      overflow-wrap: break-word;
    }

    ._stopped {
      color: $font-color-secondary;
      font-style: italic;
    }

    &.-sending {
      opacity: .6;
    }

    &.-user {
      display: flex;
      flex-direction: column;
      align-items: flex-end;

      ._attachments {
        max-width: 85%;
      }

      ._body {
        max-width: 85%;
        padding: .6rem 1.2rem;
        background-color: $hover-color;
        border-radius: $default-border-radius;
      }
    }
  }
</style>
